import { deepCloneRecord } from 'utils/utils';

/** Lottie JSON uses RGB components in 0–1 range. */

export const hexToLottieRgb = (hex: string): [number, number, number] => {
  if (!/^#[0-9a-fA-F]{6}$/.test(hex)) {
    throw new Error(`hexToLottieRgb expected a color in #RRGGBB format, received: "${hex}"`);
  }
  const h = hex.slice(1);
  return [parseInt(h.slice(0, 2), 16) / 255, parseInt(h.slice(2, 4), 16) / 255, parseInt(h.slice(4, 6), 16) / 255];
};

const isStaticWhiteColor = (k: unknown): k is [number, number, number] => {
  return (
    Array.isArray(k) &&
    k.length >= 3 &&
    typeof k[0] === 'number' &&
    typeof k[1] === 'number' &&
    typeof k[2] === 'number' &&
    k[0] >= 0.99 &&
    k[1] >= 0.99 &&
    k[2] >= 0.99
  );
};

/**
 * Replaces static white (`[1,1,1]`) fills and strokes so the logo can follow light/dark.
 * Leaves other colors (e.g. mask reds) unchanged.
 */
export const recolorLottieWhiteToRgb = (
  data: Record<string, unknown>,
  rgb: [number, number, number],
): Record<string, unknown> => {
  const parsed = deepCloneRecord(data);

  const walk = (node: unknown): void => {
    if (!node || typeof node !== 'object') return;
    if (Array.isArray(node)) {
      node.forEach(walk);
      return;
    }
    const obj = node as Record<string, unknown>;
    if ((obj.ty === 'st' || obj.ty === 'fl') && obj.c && typeof obj.c === 'object') {
      const c = obj.c as Record<string, unknown>;
      if (c.a === 0 && isStaticWhiteColor(c.k)) {
        c.k = [...rgb];
      }
    }
    Object.values(obj).forEach(walk);
  };

  walk(parsed);
  return parsed;
};

/** Replaces every static fill and stroke color while preserving animation and opacity data. */
export const recolorLottieStaticColorsToRgb = (
  data: Record<string, unknown>,
  rgb: [number, number, number],
): Record<string, unknown> => {
  const parsed = deepCloneRecord(data);

  const walk = (node: unknown): void => {
    if (!node || typeof node !== 'object') return;
    if (Array.isArray(node)) {
      node.forEach(walk);
      return;
    }
    const obj = node as Record<string, unknown>;
    if ((obj.ty === 'st' || obj.ty === 'fl') && obj.c && typeof obj.c === 'object') {
      const color = obj.c as Record<string, unknown>;
      if (color.a === 0 && Array.isArray(color.k) && color.k.length >= 3) {
        color.k = [...rgb];
      }
    }
    Object.values(obj).forEach(walk);
  };

  walk(parsed);
  return parsed;
};
